
/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/**
 * Klasse FiltertoolInstantie
 * Vertegenwoordigt één losstaande filterinstantie met logging, een geordende set regels en events.
 */
export class FiltertoolInstantie {
    // Properties
    readonly id: string;

    /**
     * Het id van het geselecteerde GebruikerFilter behorend bij deze filterinstantie.
     * Wordt gebruikt om de selectie persistent mee op te slaan naast de boom.
     */
    private _geselecteerdFilterId: number | null = null;

    private readonly toegepastBron = new Subject<void>();
    /**
     * Eventstream die aangeeft dat de huidige instelling(en) zijn toegepast.
     */
    readonly toegepast$ = this.toegepastBron.asObservable();


    private readonly boomGewijzigdBron = new Subject<Filterboom>();
    /**
     * Eventstream die afvuurt zodra de boom wijzigt (structuur of inhoud).
     */
    readonly boomGewijzigd$ = this.boomGewijzigdBron.asObservable();

    private readonly log: Sihwlog;
    /**
     * De Apiservice is beschikbaar voor eventuele backend-calls (vanuit de factory aangereikt).
     */
    private readonly api: ApiService;

    /**
     * De actuele filterboom (EN op root, met geneste OF-groepen mogelijk).
     */
    private _boom: Filterboom = maakLegeEnRoot();

    // Constructor
    /**
     * Maakt een nieuwe instantie met logging en een referentie naar de ApiService.
     */
    constructor(id: string, api: ApiService) {
        this.id = id;
        this.log = new Sihwlog('debug', `FiltertoolInstantie:${id}`);
        this.api = api;
        this.log.debug('Aangemaakt');
        // Initieer de boom als lege EN-root
        this._boom = maakLegeEnRoot();
    }

    // Public methods

    /**
     * Geeft of zet het id van het geselecteerde GebruikerFilter dat bij deze filterinstantie hoort.
     * Hiermee kan de UI de selectie vastleggen zodat de factory het samen met de boom kan opslaan.
     */
    get geselecteerdFilterId(): number | null {
        return this._geselecteerdFilterId;
    }
    set geselecteerdFilterId(v: number | null) {
        this._geselecteerdFilterId = (Number.isFinite(v as any) && (v as any) > 0) ? Number(v) : null;
    }


    /**
     * Geeft een deep clone van de huidige filterboom terug.
     */
    get boom(): Filterboom {
        return cloneDeep(this._boom);
    }

    /**
     * Stelt de huidige boom in op een deep clone van de meegegeven boom en meldt de wijziging.
     */
    set boom(nieuweBoom: Filterboom) {
        if (!nieuweBoom) {
            return;
        }
        this._boom = cloneDeep(nieuweBoom);
        this.meldGewijzigd();
    }

    /**
     * Triggert dat de huidige instellingen worden toegepast. Er wordt geen payload meegestuurd.
     */
    pasToe(): void {
        this.log.debug('Toepassen getriggerd');
        this.toegepastBron.next();
    }

    /////////////// private helpers //////////////////
    /**
     * Meldt aan alle luisteraars dat de regels zijn gewijzigd en geeft de actuele lijst mee.
     */
    private meldGewijzigd(): void {
        this.boomGewijzigdBron.next(this.boom);
    }

    /**
     * Voert de boomtraversie uit voor een generiek predicaat dat een regel evalueert.
     * Het predicaat wordt alleen aangeroepen voor geldige regels; ongeldige regels worden genegeerd (true).
     * Regels die het doeltype niet ondersteunen moeten door het predicaat op true uitkomen om ze te negeren.
     */
    private evalueerBoom(node: Filterclausule | undefined, predicaat: (regel: Filtertoolregel) => boolean): boolean {
        if (!node) {
            return true;
        }
        switch (node.soort) {
            case 'regel': {
                const regel = (node as FilterRegelClausule).regel as Filtertoolregel;
                if (!(regel as any)?.valid) {
                    return true; // ongeldige regel negeren
                }
                return predicaat(regel);
            }
            case 'groep': {
                const g = node as FilterGroep;
                switch (g.operator) {
                    case 'en':
                        for (const kind of g.kinderen) {
                            if (!this.evalueerBoom(kind, predicaat)) {
                                return false;
                            }
                        }
                        return true;
                    case 'of':
                        for (const kind of g.kinderen) {
                            if (this.evalueerBoom(kind, predicaat)) {
                                return true;
                            }
                        }
                        return false;
                }
            }
        }
    }


    /**
     * Schrijft de huidige boom weg als platte objecten, geschikt voor JSON.
     */
    public boomNaarObject(): any {
        return filterboomNaarObject(this._boom);
    }

    /**
     * Laad een boom vanuit platte objecten en stel deze in als actuele boom.
     */
    public laadBoomVanObject(obj: any): void {
        this._boom = filterboomVanObject(obj);
        this.meldGewijzigd();
    }

    ///////////////////////////////// FILTERS ////////////////////////////////////////////
    /**
     * Bepaalt of het meegegeven planitem matcht op basis van de huidige filterboom.
     * Nieuwe definitie:
     * 1) Alleen regels in filtercontext 'planitem' worden op het planitem toegepast.
     * 2) Daarnaast moet er minimaal één PPI (planitem + personeelslid) zijn die voldoet aan alle
     *    regels in filtercontext 'personeelplanitem' (via filterPPI).
     * Regels uit een andere context worden in deze functie genegeerd.
     * Met legeDienstToestaan wordt een planitem zonder PPI's beoordeeld alsof er één PPI zonder
     * personeelslid is: regels op personeel of label matchen dan niet, regels uit andere contexten
     * negeren de personeelplanitem-stap. Zonder die vlag valt zo'n planitem altijd af.
     */
    public filterPlanitem(pi: PlanitemType, legeDienstToestaan = false): boolean {
        // Eerst: planitem-context moet matchen
        const planitemMatch = this.evalueerBoom(this._boom, (regel) => {
            const cat = this.regelCategorie(regel);
            if (cat === 'planitem') {
                return heeftPlanitemFilter(regel) ? (regel as any).filterPlanitem(pi) === true : true;
            }
            // Andere contexten negeren we in deze stap
            return true;
        });
        if (!planitemMatch) { return false; }

        // Vervolgens: er moet minstens 1 PPI zijn die slaagt voor de personeelplanitem-context
        const ppis = (pi as any)?.personeel_planitem || [];
        // Een onbezette dienst toetsen we op de personeelplanitem-context met een leeg personeelslid
        if (!ppis.length && legeDienstToestaan) { return this.filterPPI(pi, null); }
        for (const ppi of ppis) {
            if (this.filterPPI(pi, ppi?.personeel)) { return true; }
        }
        // Geen enkele PPI haalt de personeelplanitem-context
        return false;
    }

    /**
     * Bepaalt of het meegegeven planitem matcht waarbij personeelsgerichte regels genegeerd worden.
     * Te gebruiken in weergaven waar mogelijke diensten getoond worden (invulling_planning),
     * zodat filters op Personeel of Personeelslabel geen planitems uitsluiten.
     */
    public filterPlanitemZonderPersoneel(pi: PlanitemType): boolean {
        return this.evalueerBoom(this._boom, (regel) => {
            // Negeer regels die op personeelsniveau filteren wanneer we planitems evalueren
            const r: any = regel as any;
            const context = (r?.context as string) || '';
            if (context === 'planitempersoneel' || context === 'personeelslabel') {
                return true; // regel negeren in deze context
            }
            return heeftPlanitemFilter(regel) ? regel.filterPlanitem(pi) : true;
        });
    }
    
    /**
     * Bepaalt of de meegegeven Functie matcht op basis van de huidige filterboom.
     * Regels zonder functie-ondersteuning worden genegeerd.
     * Dit betekent dat als een functieregel ergens in een OF zit, alle functies matchen
     * Dat is dus niet nuttig. We MOETEN gewoon van planitems uitgaan
     */
    public filterFunctie(f: FunctieType): boolean {
        return this.evalueerBoom(this._boom, (regel) => heeftFunctieFilter(regel) ? (regel as any).filterFunctie(f) : true);
    }
    
    /**
     * Bepaalt of het meegegeven Personeel matcht op basis van de huidige filterboom.
     * Regels zonder personeel-ondersteuning worden genegeerd.
     */
    public filterPersoneel(persoon: PersoneelType): boolean {
        return this.evalueerBoom(this._boom, (regel) => heeftPersoneelFilter(regel) ? (regel as any).filterPersoneel(persoon) : true);
    }

    /**
     * Bepaalt of een combinatie van Planitem en Personeel (PPI-context) matcht op basis van de volledige boom.
     * De evaluatie gebeurt per regel als volgt:
     * - Heeft de regel alleen planitem-ondersteuning: evalueer op het planitem.
     * - Heeft de regel alleen personeel-ondersteuning: evalueer op het personeelslid.
     * - Heeft de regel beide: evalueer op beide en accepteer wanneer één van beide waar is.
     * - Regels zonder toepasselijke ondersteuning worden genegeerd (true).
     */
    public filterPPI(pi: PlanitemType, persoon: PersoneelType | null | undefined): boolean {
        return this.evalueerBoom(this._boom, (regel) => {
            // Neem in PPI-context uitsluitend regels mee die in de categorie 'personeelplanitem' vallen
            const cat = this.regelCategorie(regel);
            if (cat !== 'personeelplanitem') { return true; }
            return heeftPersoneelFilter(regel) ? (regel as any).filterPersoneel(persoon) === true : true;
        });
    }

    /**
     * Bepaalt de filtercontext-categorie van een regel op basis van zijn type/context.
     * - 'planitem' voor regels die op het planitem slaan (bijv. functie, project, vrij)
     * - 'personeelplanitem' voor regels die op personeelsniveau binnen een planitem slaan (bijv. personeel, personeelslabel)
     */
    private regelCategorie(regel: Filtertoolregel): 'planitem' | 'personeelplanitem' {
        const ctx = String((regel as any)?.context || '').toLowerCase();
        switch (ctx) {
            case 'planitemfunctie':
            case 'planitemfunctionalias':
            case 'planitemongekwalificeerd':
            case 'project':
            case 'vrij':
                return 'planitem';
            case 'planitempersoneel':
            case 'personeelslabel':
                return 'personeelplanitem';
            default:
                // Onbekend: conservatief negeren -> behandel als planitem zodat het geen PPI's ten onrechte uitsluit
                return 'planitem';
        }
    }
}

/*
 * filtertool-instantie.ts
 * Gegenereerd met Junie (AI), 29-09-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Puur raamwerk voor één filtertool-instantie, met beheer van geordende regels en events.
 */

import {Subject} from 'rxjs';
import {Sihwlog} from 'sihw-ngx';
import {ApiService} from '../api/api.service';
import {
    Filterboom,
    FilterRegelClausule,
    maakLegeEnRoot,
    FilterGroep,
    filterboomNaarObject,
    filterboomVanObject,
    Filterclausule
} from './filtertool-structuur';
import {cloneDeep} from 'lodash-es';
import type {Planitem as PlanitemType} from '../../models/planitem';
import type { Functie as FunctieType } from '../../models/functie';
import type { Personeel as PersoneelType } from '../../models/personeel';
import type { Filtertoolregel } from './filtertool-regels';
import {heeftPlanitemFilter, heeftFunctieFilter, heeftPersoneelFilter} from './filtertool-capabilities';
